import React from 'react';
import { ToastContainer, toast as toastify, cssTransition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './NotificationToast.css';
import { haptic } from '../../utils/haptics';

/**
 * Toasts der App: react-toastify als Unterbau (unverändert), das Aussehen
 * kommt vollständig aus NotificationToast.css und der eigenen Transition.
 * Seit Oktober 2026 flach und deckend aus Tokens statt Glas (TOKENS.md,
 * „Offene Entscheidungen“ Punkt 1, entschieden am 20.10.2026).
 *
 * WANN EIN TOAST ERLAUBT IST (Toast-Noise-Regel, Sep 2026 - siehe auch
 * docs/design-guide/CorporateDesign.md, Abschnitt "Feedback"):
 *
 *   1. Fehler, der nicht inline am Formular steht.
 *   2. Ergebnis auf dem aktuellen Bildschirm unsichtbar ("Kopiert", Download gestartet).
 *   3. Ereignis von aussen (Host hat Session beendet, Sitzung abgelaufen, neue Nachricht).
 *   4. Formular bleibt nach dem Speichern offen (Admin-Konfigurationen).
 *
 * KEIN Toast, wenn danach navigiert wird, eine Liste sich aktualisiert oder ein
 * Schalter sichtbar umspringt - die Oberflaeche ist dann selbst das Feedback.
 * Keine Technik im Text (ms, Statuscodes), keine "Lade..."-Toasts.
 *
 * HAPTIK (Oktober 2026): `toast.error` löst selbst einmal `haptic.error()`
 * aus. Wer `toast.error` ruft, ergänzt deshalb KEIN eigenes `haptic.error()`
 * im selben Pfad — sonst vibriert es doppelt. Abschalten mit
 * `{ haptic: false }` (z. B. für Fehler aus Hintergrund-Abrufen). Ist ein
 * Toast mit derselben `toastId` schon sichtbar, zeigt react-toastify keinen
 * zweiten — dann bleibt auch der Impuls aus. Zwei Fehler-Toasts innerhalb
 * einer Sekunde (auch mit verschiedener toastId) ergeben ebenfalls nur einen
 * Impuls.
 */

const ToastTransition = cssTransition({
  enter: 'eo-toast-in',
  exit: 'eo-toast-out',
  collapseDuration: 250,
});

const DEFAULT_OPTIONS = {
  autoClose: 3000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: false,
  draggable: true,
};

/**
 * Meldungen der Form „Titel\nText“ bekommen eine Hierarchie: Die erste Zeile
 * ist der Titel, der Rest der Text darunter. Vorher stand beides als ein
 * nackter Textknoten mit Zeilenumbruch da, gleich laut. Alles andere
 * (Strings ohne Umbruch, Elemente, Render-Funktionen) bleibt unverändert.
 */
export const formatToastMessage = (message) => {
  if (typeof message !== 'string') return message;
  const breakAt = message.indexOf('\n');
  if (breakAt === -1) return message;
  const title = message.slice(0, breakAt).trim();
  const text = message.slice(breakAt + 1).trim();
  if (!title || !text) return title || text;
  return (
    <div className="min-w-0">
      <p className="font-semibold text-foreground">{title}</p>
      <p className="text-muted-foreground">{text}</p>
    </div>
  );
};

// Zeitfenster, in dem ein zweiter Fehler-Toast keinen zweiten Impuls auslöst.
// Typischer Fall: Der Axios-Interceptor zeigt über ErrorHandler einen Toast
// (toastId `apierror-…`), und der Aufrufer zeigt danach seinen eigenen ohne
// toastId („Lernziel konnte nicht erstellt werden“). Beides ist derselbe
// Fehler, also genau ein Impuls (Leitlinie 8, Gerätetest „nicht zwei“).
export const ERROR_HAPTIC_DEBOUNCE_MS = 1000;
let lastErrorHapticAt = -Infinity;

// Optional verkettet, weil Tests `utils/haptics` oft nur teilweise mocken.
const errorHaptic = (options) => {
  const { haptic: wantsHaptic = true, toastId } = options;
  if (!wantsHaptic) return;
  if (toastId != null && toastify.isActive?.(toastId)) return;
  const now = Date.now();
  if (now - lastErrorHapticAt < ERROR_HAPTIC_DEBOUNCE_MS) return;
  lastErrorHapticAt = now;
  haptic?.error?.();
};

// `haptic` ist eine eigene Option dieses Wrappers und wird nicht an
// react-toastify weitergereicht.
const withoutHapticOption = ({ haptic: _haptic, ...rest }) => rest;

// Export clean wrapper functions
export const toast = {
  success: (message, options = {}) =>
    toastify.success(formatToastMessage(message), { ...DEFAULT_OPTIONS, ...withoutHapticOption(options) }),

  error: (message, options = {}) => {
    errorHaptic(options);
    return toastify.error(formatToastMessage(message), { ...DEFAULT_OPTIONS, ...withoutHapticOption(options) });
  },

  info: (message, options = {}) =>
    toastify.info(formatToastMessage(message), { ...DEFAULT_OPTIONS, ...withoutHapticOption(options) }),

  warning: (message, options = {}) =>
    toastify.warning(formatToastMessage(message), { ...DEFAULT_OPTIONS, ...withoutHapticOption(options) }),
};

export const NotificationToastContainer = ({ position = "top-center", ...props }) => {
  return (
    <ToastContainer
      className="eo-toast"
      transition={ToastTransition}
      position={position}
      autoClose={3000}
      hideProgressBar
      newestOnTop
      closeOnClick
      rtl={false}
      pauseOnFocusLoss={false}
      draggable
      pauseOnHover
      theme="light"
      // Hoechstens zwei gleichzeitig: Ab dem dritten wirkt es wie Spam, und
      // wer drei Meldungen braucht, hat ein anderes Problem als Platzmangel.
      limit={2}
      {...props}
    />
  );
};

export default NotificationToastContainer;
